import React, { useState } from 'react';

interface IrregularityAnalysis {
  isIrregular: boolean;
  irregularityType: 'length_deviation' | 'consecutive_irregular' | null;
  severity: 'low' | 'medium' | 'high' | null;
  deviationDays: number | null;
  consecutiveCount: number;
  recommendMedicalConsultation: boolean;
}

interface IrregularityAlertProps {
  irregularity: IrregularityAnalysis;
  cycleDate: string;
  onDismiss?: () => void;
}

const IrregularityAlert: React.FC<IrregularityAlertProps> = ({
  irregularity,
  cycleDate,
  onDismiss,
}) => {
  const [showDetails, setShowDetails] = useState(false);

  if (!irregularity.isIrregular) {
    return null;
  }

  const getSeverityColor = () => {
    switch (irregularity.severity) {
      case 'high':
        return 'bg-red-100 border-red-300 text-red-800';
      case 'medium':
        return 'bg-orange-100 border-orange-300 text-orange-800';
      case 'low':
        return 'bg-yellow-100 border-yellow-300 text-yellow-800';
      default:
        return 'bg-gray-100 border-gray-300 text-gray-800';
    }
  };

  const getSeverityIcon = () => {
    switch (irregularity.severity) {
      case 'high':
        return '🚨';
      case 'medium':
        return '⚠️';
      case 'low':
        return '⚡';
      default:
        return '⚠️';
    }
  };

  const getIrregularityMessage = () => {
    if (irregularity.irregularityType === 'consecutive_irregular') {
      return `You've had ${irregularity.consecutiveCount} consecutive irregular cycles. This pattern may indicate a health concern.`;
    }
    
    if (irregularity.deviationDays) {
      return `This cycle deviated by ${irregularity.deviationDays} days from your average cycle length.`;
    }
    
    return 'An irregularity was detected in this cycle.';
  };

  return (
    <div className={`rounded-xl border-2 p-4 ${getSeverityColor()}`}>
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3 flex-1">
          <span className="text-2xl">{getSeverityIcon()}</span>
          <div className="flex-1">
            <h4 className="font-semibold mb-1">Cycle Irregularity Detected</h4>
            <p className="text-sm mb-2">{getIrregularityMessage()}</p>
            
            {irregularity.recommendMedicalConsultation && (
              <div className="mt-3 p-3 bg-white bg-opacity-50 rounded-lg">
                <p className="text-sm font-medium">
                  💡 We recommend consulting with a healthcare provider about this pattern.
                </p>
              </div>
            )}
            
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="mt-3 text-sm font-medium underline hover:no-underline"
            >
              {showDetails ? 'Hide Details' : 'Learn More'}
            </button>
            
            {showDetails && (
              <div className="mt-4 p-4 bg-white bg-opacity-70 rounded-lg space-y-3">
                <div>
                  <h5 className="font-semibold text-sm mb-2">What is cycle irregularity?</h5>
                  <p className="text-sm">
                    A menstrual cycle is considered irregular when it varies significantly from your average cycle length. 
                    Variations of more than 7 days can indicate hormonal imbalances, stress, lifestyle changes, or other health conditions.
                  </p>
                </div>
                
                <div>
                  <h5 className="font-semibold text-sm mb-2">Common causes:</h5>
                  <ul className="text-sm list-disc list-inside space-y-1">
                    <li>Stress and anxiety</li>
                    <li>Significant weight changes</li>
                    <li>Hormonal imbalances (PCOS, thyroid issues)</li>
                    <li>Changes in exercise routine</li>
                    <li>Certain medications</li>
                    <li>Perimenopause</li>
                  </ul>
                </div>
                
                <div>
                  <h5 className="font-semibold text-sm mb-2">When to see a doctor:</h5>
                  <ul className="text-sm list-disc list-inside space-y-1">
                    <li>3 or more consecutive irregular cycles</li>
                    <li>Cycles shorter than 21 days or longer than 35 days</li>
                    <li>Severe pain or heavy bleeding</li>
                    <li>Missed periods (if not pregnant)</li>
                    <li>Any concerning symptoms</li>
                  </ul>
                </div>
                
                <div className="pt-3 border-t border-current border-opacity-20">
                  <p className="text-xs italic">
                    This information is for educational purposes only and not intended as medical advice. 
                    Always consult with a healthcare professional for medical concerns.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
        
        {onDismiss && (
          <button
            onClick={onDismiss}
            className="ml-2 text-current opacity-50 hover:opacity-100 transition-opacity"
            aria-label="Dismiss"
          >
            ✕
          </button>
        )}
      </div>
    </div>
  );
};

export default IrregularityAlert;
